// מסך פרופיל ויעדים — טופס נתונים אישיים, תצוגת יעדים חיה ושמירה ל-litebite_profiles.
// כל ה-HTML המוזרק הוא מחרוזות סטטיות; ערכי משתמש נכנסים רק דרך value/textContent.
import './profile.css';
import { supabase } from '../lib/supabase';
import { invalidateTargets } from '../services/entries';
import {
  CALORIE_FLOOR,
  calcAge,
  calcTargets,
  type ActivityLevel,
  type GoalsInput,
  type Sex,
} from '../lib/goals';

// ---------- קבועים ----------

const ACTIVITY_OPTIONS: { value: ActivityLevel; label: string }[] = [
  { value: 'sedentary', label: 'יושבנית — עבודה משרדית, מעט תנועה' },
  { value: 'light', label: 'קלה — הליכות או אימון 1–3 בשבוע' },
  { value: 'moderate', label: 'בינונית — אימון 3–5 בשבוע' },
  { value: 'high', label: 'גבוהה — אימון אינטנסיבי 6–7 בשבוע' },
];

const RATE_OPTIONS = [
  { value: '0.25', label: '0.25 ק"ג בשבוע · מתון' },
  { value: '0.5', label: '0.5 ק"ג בשבוע · מומלץ' },
  { value: '0.75', label: '0.75 ק"ג בשבוע · נמרץ' },
];

const LIMITS = {
  height: { min: 120, max: 230 },
  weight: { min: 35, max: 250 },
  age: { min: 14, max: 100 },
};

type FieldKey = 'sex' | 'birthdate' | 'height' | 'weight' | 'activity' | 'goalWeight';

interface FormRead {
  /** מלא רק כשכל השדות קיימים ותקינים */
  input: GoalsInput | null;
  /** שדות שמולאו אך לא תקינים — מוצגים גם בעדכון חי */
  rangeErrors: Partial<Record<FieldKey, string>>;
  /** שדות חסרים — מוצגים רק בניסיון שמירה */
  missingErrors: Partial<Record<FieldKey, string>>;
}

/** שורת litebite_profiles כפי שחוזרת מה-select (עמודות ההזנה בלבד) */
interface ProfileRow {
  sex: string | null;
  birthdate: string | null;
  height_cm: number | null;
  current_weight_kg: number | null;
  goal_weight_kg: number | null;
  weekly_rate_kg: number | null;
  activity_level: string | null;
  manual_targets: boolean | null;
  calorie_target: number | null;
  protein_target_g: number | null;
  carbs_target_g: number | null;
  fat_target_g: number | null;
}

// ---------- עזרי DOM קטנים ----------

function q<T extends HTMLElement = HTMLElement>(root: HTMLElement, sel: string): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`profile: לא נמצא ${sel}`);
  return el;
}

/** מספר מהשדה, או null כשריק/לא מספרי */
function readNumber(root: HTMLElement, sel: string): number | null {
  const v = q<HTMLInputElement>(root, sel).value.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const fmt = (n: number): string => n.toLocaleString('he-IL');

/** תאריך היום המקומי כ-YYYY-MM-DD (לא toISOString — סטיות אזור זמן) */
function todayLocalIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ---------- רינדור ----------

export function renderProfile(): string {
  const activityOptions = ACTIVITY_OPTIONS.map(
    (o) => `<option value="${o.value}">${o.label}</option>`,
  ).join('');
  const rateOptions = RATE_OPTIONS.map(
    (o) => `<option value="${o.value}"${o.value === '0.5' ? ' selected' : ''}>${o.label}</option>`,
  ).join('');

  return `
  <div class="pf">
    <h2 class="pf-title">פרופיל ויעדים</h2>

    <div class="card">
      <div class="pf-section-title">פרטים אישיים</div>
      <div class="pf-field">
        <span class="pf-label">מין</span>
        <div class="pf-seg" role="radiogroup" aria-label="מין">
          <button type="button" class="pf-seg-btn" data-value="female">נקבה</button>
          <button type="button" class="pf-seg-btn" data-value="male">זכר</button>
        </div>
        <div class="pf-err" data-err="sex"></div>
      </div>
      <div class="pf-field">
        <label class="pf-label" for="pf-birthdate">תאריך לידה</label>
        <input class="pf-input" id="pf-birthdate" type="date" max="${todayLocalIso()}">
        <div class="pf-err" data-err="birthdate"></div>
      </div>
      <div class="pf-grid2">
        <div class="pf-field">
          <label class="pf-label" for="pf-height">גובה (ס"מ)</label>
          <input class="pf-input" id="pf-height" type="number" inputmode="decimal"
            min="${LIMITS.height.min}" max="${LIMITS.height.max}" placeholder="165">
          <div class="pf-err" data-err="height"></div>
        </div>
        <div class="pf-field">
          <label class="pf-label" for="pf-weight">משקל נוכחי (ק"ג)</label>
          <input class="pf-input" id="pf-weight" type="number" inputmode="decimal" step="0.1"
            min="${LIMITS.weight.min}" max="${LIMITS.weight.max}" placeholder="70">
          <div class="pf-err" data-err="weight"></div>
        </div>
      </div>
      <div class="pf-field">
        <label class="pf-label" for="pf-activity">רמת פעילות</label>
        <select class="pf-select" id="pf-activity">
          <option value="" disabled selected>בחרו רמת פעילות</option>
          ${activityOptions}
        </select>
        <div class="pf-err" data-err="activity"></div>
      </div>
    </div>

    <div class="card">
      <div class="pf-section-title">יעד הירידה</div>
      <div class="pf-field">
        <label class="pf-label" for="pf-goal-weight">משקל יעד (ק"ג)</label>
        <input class="pf-input" id="pf-goal-weight" type="number" inputmode="decimal" step="0.1"
          min="${LIMITS.weight.min}" max="${LIMITS.weight.max}" placeholder="62">
        <div class="pf-err" data-err="goalWeight"></div>
      </div>
      <div class="pf-field">
        <label class="pf-label" for="pf-rate">קצב ירידה שבועי</label>
        <select class="pf-select" id="pf-rate">${rateOptions}</select>
      </div>
    </div>

    <div class="card pf-targets">
      <div class="pf-section-title">היעדים היומיים שלך</div>
      <div id="pf-targets-body" hidden>
        <div class="pf-cal">
          <span class="pf-cal-num" id="pf-cal-num"></span>
          <span class="pf-cal-unit">קק"ל ביום</span>
        </div>
        <div class="pf-macros">
          <div class="pf-macro">
            <div class="pf-macro-name"><span class="pf-dot" style="background:var(--protein)"></span>חלבון</div>
            <b id="pf-protein"></b>
          </div>
          <div class="pf-macro">
            <div class="pf-macro-name"><span class="pf-dot" style="background:var(--carbs)"></span>פחמימות</div>
            <b id="pf-carbs"></b>
          </div>
          <div class="pf-macro">
            <div class="pf-macro-name"><span class="pf-dot" style="background:var(--fat)"></span>שומן</div>
            <b id="pf-fat"></b>
          </div>
        </div>
        <div class="pf-clamp" id="pf-clamp" hidden></div>
      </div>
      <div class="muted" id="pf-targets-empty">משלימים את הפרטים למעלה — והיעדים יחושבו כאן מיד.</div>
    </div>

    <div class="card pf-manual">
      <label class="pf-manual-toggle">
        <span><b>לקבוע יעדים בעצמי</b><small>במקום החישוב האוטומטי</small></span>
        <input type="checkbox" id="pf-manual-on">
      </label>
      <div id="pf-manual-fields" hidden>
        <div class="pf-grid2">
          <div class="pf-field">
            <label class="pf-label" for="pf-m-cal">קלוריות ליום</label>
            <input class="pf-input" id="pf-m-cal" type="number" inputmode="numeric" min="800" max="6000" placeholder="1650">
          </div>
          <div class="pf-field">
            <label class="pf-label" for="pf-m-p">חלבון (ג')</label>
            <input class="pf-input" id="pf-m-p" type="number" inputmode="numeric" min="0" max="500" placeholder="120">
          </div>
          <div class="pf-field">
            <label class="pf-label" for="pf-m-c">פחמימות (ג')</label>
            <input class="pf-input" id="pf-m-c" type="number" inputmode="numeric" min="0" max="800" placeholder="160">
          </div>
          <div class="pf-field">
            <label class="pf-label" for="pf-m-f">שומן (ג')</label>
            <input class="pf-input" id="pf-m-f" type="number" inputmode="numeric" min="0" max="400" placeholder="50">
          </div>
        </div>
      </div>
    </div>

    <button type="button" class="pf-save" id="pf-save">שמירת הפרופיל</button>
    <div class="pf-status" id="pf-status"></div>
  </div>`;
}

// ---------- קריאת הטופס וולידציה ----------

function readForm(root: HTMLElement): FormRead {
  const rangeErrors: FormRead['rangeErrors'] = {};
  const missingErrors: FormRead['missingErrors'] = {};

  const sexBtn = root.querySelector<HTMLButtonElement>('.pf-seg-btn.on');
  const sex = sexBtn?.dataset.value === 'male' ? 'male' : sexBtn ? 'female' : null;
  if (!sex) missingErrors.sex = 'בחרו מין';

  const birthdate = q<HTMLInputElement>(root, '#pf-birthdate').value;
  if (!birthdate) {
    missingErrors.birthdate = 'הזינו תאריך לידה';
  } else {
    const age = calcAge(birthdate);
    if (!Number.isFinite(age) || age < LIMITS.age.min || age > LIMITS.age.max) {
      rangeErrors.birthdate = `התאריך לא סביר — גיל בין ${LIMITS.age.min} ל-${LIMITS.age.max}`;
    }
  }

  const height = readNumber(root, '#pf-height');
  if (height === null) missingErrors.height = 'הזינו גובה';
  else if (height < LIMITS.height.min || height > LIMITS.height.max) {
    rangeErrors.height = `גובה בין ${LIMITS.height.min} ל-${LIMITS.height.max} ס"מ`;
  }

  const weight = readNumber(root, '#pf-weight');
  if (weight === null) missingErrors.weight = 'הזינו משקל נוכחי';
  else if (weight < LIMITS.weight.min || weight > LIMITS.weight.max) {
    rangeErrors.weight = `משקל בין ${LIMITS.weight.min} ל-${LIMITS.weight.max} ק"ג`;
  }

  const goalWeight = readNumber(root, '#pf-goal-weight');
  if (goalWeight === null) missingErrors.goalWeight = 'הזינו משקל יעד';
  else if (goalWeight < LIMITS.weight.min || goalWeight > LIMITS.weight.max) {
    rangeErrors.goalWeight = `משקל בין ${LIMITS.weight.min} ל-${LIMITS.weight.max} ק"ג`;
  } else if (weight !== null && !rangeErrors.weight && goalWeight > weight) {
    rangeErrors.goalWeight = 'משקל היעד גבוה מהמשקל הנוכחי';
  }

  const activityValue = q<HTMLSelectElement>(root, '#pf-activity').value;
  const activity = ACTIVITY_OPTIONS.find((o) => o.value === activityValue)?.value ?? null;
  if (!activity) missingErrors.activity = 'בחרו רמת פעילות';

  const weeklyRateKg = Number(q<HTMLSelectElement>(root, '#pf-rate').value);

  const valid =
    Object.keys(rangeErrors).length === 0 &&
    Object.keys(missingErrors).length === 0 &&
    sex !== null && height !== null && weight !== null && goalWeight !== null && activity !== null;

  const input: GoalsInput | null = valid
    ? {
        sex: sex as Sex,
        birthdate,
        heightCm: height as number,
        currentWeightKg: weight as number,
        goalWeightKg: goalWeight as number,
        activityLevel: activity as ActivityLevel,
        weeklyRateKg,
      }
    : null;

  return { input, rangeErrors, missingErrors };
}

function paintErrors(root: HTMLElement, errors: Partial<Record<FieldKey, string>>): void {
  root.querySelectorAll<HTMLElement>('.pf-err').forEach((el) => {
    const key = el.dataset.err as FieldKey | undefined;
    el.textContent = (key && errors[key]) || '';
  });
}

// ---------- תצוגת היעדים החיה ----------

function updateTargets(root: HTMLElement): void {
  const { input } = readForm(root);
  const body = q(root, '#pf-targets-body');
  const empty = q(root, '#pf-targets-empty');
  if (!input) {
    body.hidden = true;
    empty.hidden = false;
    return;
  }
  const t = calcTargets(input);
  body.hidden = false;
  empty.hidden = true;
  q(root, '#pf-cal-num').textContent = fmt(t.calories);
  q(root, '#pf-protein').textContent = `${fmt(t.proteinG)} ג'`;
  q(root, '#pf-carbs').textContent = `${fmt(t.carbsG)} ג'`;
  q(root, '#pf-fat').textContent = `${fmt(t.fatG)} ג'`;

  const clampEl = q(root, '#pf-clamp');
  clampEl.hidden = !t.clamped;
  if (t.clamped) {
    clampEl.textContent =
      `היעד נעצר ברצפת הבטיחות — ${fmt(CALORIE_FLOOR[input.sex])} קק"ל ביום. ` +
      'ירידה מתחת לזה לא בריאה, אז הקצב בפועל יהיה מעט איטי מהנבחר.';
  }
}

/** עדכון חי: שגיאות טווח בלבד (שדות חסרים מסומנים רק בשמירה) + כרטיס היעדים */
function onFormChange(root: HTMLElement): void {
  const { rangeErrors } = readForm(root);
  paintErrors(root, rangeErrors);
  updateTargets(root);
}

// ---------- חיווי סטטוס ----------

let statusTimer: number | undefined;

function setStatus(root: HTMLElement, text: string, kind: '' | 'ok' | 'err', clearAfterMs?: number): void {
  const el = q(root, '#pf-status');
  el.textContent = text;
  el.className = `pf-status${kind ? ` ${kind}` : ''}`;
  if (statusTimer !== undefined) window.clearTimeout(statusTimer);
  if (clearAfterMs) {
    statusTimer = window.setTimeout(() => {
      el.textContent = '';
    }, clearAfterMs);
  }
}

// ---------- טעינה ושמירה ----------

async function load(root: HTMLElement): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user || !root.isConnected) return;
  const { data } = await supabase
    .from('litebite_profiles')
    .select('sex, birthdate, height_cm, current_weight_kg, goal_weight_kg, activity_level, weekly_rate_kg, manual_targets, calorie_target, protein_target_g, carbs_target_g, fat_target_g')
    .eq('user_id', auth.user.id)
    .maybeSingle();
  if (!data || !root.isConnected) return;
  fillForm(root, data as ProfileRow);
  onFormChange(root);
  // שחזור מצב יעדים ידניים
  if ((data as ProfileRow).manual_targets) {
    const r = data as ProfileRow;
    q<HTMLInputElement>(root, '#pf-manual-on').checked = true;
    q(root, '#pf-manual-fields').hidden = false;
    const setv = (sel: string, v: number | null) => { if (v != null) q<HTMLInputElement>(root, sel).value = String(v); };
    setv('#pf-m-cal', r.calorie_target);
    setv('#pf-m-p', r.protein_target_g);
    setv('#pf-m-c', r.carbs_target_g);
    setv('#pf-m-f', r.fat_target_g);
  }
}

function fillForm(root: HTMLElement, row: ProfileRow): void {
  if (row.sex === 'female' || row.sex === 'male') {
    root.querySelectorAll<HTMLButtonElement>('.pf-seg-btn').forEach((b) => {
      b.classList.toggle('on', b.dataset.value === row.sex);
    });
  }
  if (row.birthdate) q<HTMLInputElement>(root, '#pf-birthdate').value = row.birthdate;
  if (row.height_cm != null) q<HTMLInputElement>(root, '#pf-height').value = String(row.height_cm);
  if (row.current_weight_kg != null) {
    q<HTMLInputElement>(root, '#pf-weight').value = String(row.current_weight_kg);
  }
  if (row.goal_weight_kg != null) {
    q<HTMLInputElement>(root, '#pf-goal-weight').value = String(row.goal_weight_kg);
  }
  const activity = ACTIVITY_OPTIONS.find((o) => o.value === row.activity_level);
  if (activity) q<HTMLSelectElement>(root, '#pf-activity').value = activity.value;
  const rate = RATE_OPTIONS.find((o) => Number(o.value) === Number(row.weekly_rate_kg));
  if (rate) q<HTMLSelectElement>(root, '#pf-rate').value = rate.value;
}

/** קריאת יעדים ידניים; null אם הקלוריות לא תקינות (חובה), מקרו ריק → 0 */
function readManualTargets(root: HTMLElement): { cal: number; p: number; c: number; f: number } | null {
  const cal = readNumber(root, '#pf-m-cal');
  if (cal === null || cal < 800 || cal > 6000) return null;
  const macro = (sel: string) => Math.max(0, Math.round(readNumber(root, sel) ?? 0));
  return { cal: Math.round(cal), p: macro('#pf-m-p'), c: macro('#pf-m-c'), f: macro('#pf-m-f') };
}

async function save(root: HTMLElement): Promise<void> {
  const manualOn = q<HTMLInputElement>(root, '#pf-manual-on').checked;
  const { input, rangeErrors, missingErrors } = readForm(root);
  paintErrors(root, { ...missingErrors, ...rangeErrors });

  // במצב ידני: הקלוריות מהשדות הידניים הן החובה; שדות הפרופיל נשמרים אם מולאו תקין.
  if (!manualOn && !input) {
    setStatus(root, 'יש להשלים את השדות המסומנים', 'err', 4000);
    return;
  }
  const manual = manualOn ? readManualTargets(root) : null;
  if (manualOn && !manual) {
    setStatus(root, 'הזינו יעד קלוריות תקין (800–6000)', 'err', 4000);
    return;
  }

  const btn = q<HTMLButtonElement>(root, '#pf-save');
  btn.disabled = true;
  setStatus(root, 'שומר…', '');
  try {
    const { data: auth, error: authError } = await supabase.auth.getUser();
    if (authError || !auth.user) {
      setStatus(root, 'צריך להתחבר כדי לשמור', 'err');
      return;
    }
    const t = manual ?? calcTargets(input!);
    const targets = manual
      ? { calorie_target: manual.cal, protein_target_g: manual.p, carbs_target_g: manual.c, fat_target_g: manual.f }
      : { calorie_target: (t as ReturnType<typeof calcTargets>).calories, protein_target_g: (t as ReturnType<typeof calcTargets>).proteinG, carbs_target_g: (t as ReturnType<typeof calcTargets>).carbsG, fat_target_g: (t as ReturnType<typeof calcTargets>).fatG };
    const profileFields = input
      ? {
          sex: input.sex, birthdate: input.birthdate, height_cm: Math.round(input.heightCm),
          current_weight_kg: input.currentWeightKg, goal_weight_kg: input.goalWeightKg,
          activity_level: input.activityLevel, weekly_rate_kg: input.weeklyRateKg,
        }
      : {};
    const { error } = await supabase.from('litebite_profiles').upsert({
      user_id: auth.user.id,
      ...profileFields,
      ...targets,
      manual_targets: manualOn,
      updated_at: new Date().toISOString(),
    });
    if (error) {
      setStatus(root, 'השמירה נכשלה — בדקו חיבור ונסו שוב', 'err');
      return;
    }
    invalidateTargets(); // היעדים השתנו — שהבית/התקדמות ישלפו מחדש
    setStatus(root, 'נשמר ✓', 'ok', 2500);
  } finally {
    btn.disabled = false;
  }
}

// ---------- חיבור מאזינים ----------

export function attachProfile(root: HTMLElement): void {
  // מין — כפתורי בחירה
  root.querySelectorAll<HTMLButtonElement>('.pf-seg-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll<HTMLButtonElement>('.pf-seg-btn').forEach((b) => {
        b.classList.toggle('on', b === btn);
      });
      onFormChange(root);
    });
  });

  // כל שינוי שדה מעדכן חיווי שגיאות + כרטיס היעדים
  root.querySelectorAll<HTMLElement>('.pf-input, .pf-select').forEach((el) => {
    el.addEventListener('input', () => onFormChange(root));
    el.addEventListener('change', () => onFormChange(root));
  });

  // מתג יעדים ידניים — הצגת/הסתרת השדות; במעבר למצב ידני, מילוי מהיעד המחושב אם ריק
  const manualToggle = q<HTMLInputElement>(root, '#pf-manual-on');
  manualToggle.addEventListener('change', () => {
    q(root, '#pf-manual-fields').hidden = !manualToggle.checked;
    if (manualToggle.checked) {
      const { input } = readForm(root);
      if (input) {
        const t = calcTargets(input);
        const setIfEmpty = (sel: string, v: number) => {
          const el = q<HTMLInputElement>(root, sel);
          if (!el.value) el.value = String(v);
        };
        setIfEmpty('#pf-m-cal', t.calories);
        setIfEmpty('#pf-m-p', t.proteinG);
        setIfEmpty('#pf-m-c', t.carbsG);
        setIfEmpty('#pf-m-f', t.fatG);
      }
    }
  });

  q<HTMLButtonElement>(root, '#pf-save').addEventListener('click', () => void save(root));

  void load(root);
}
